/** Öffentliche Seiten Impressum und Datenschutz (E8): zeigen nur vom Anbieter freigegebene Texte. */
import { Link, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Seitenzustand } from "../ui";
import { Marke } from "../ui/Marke";
import { api, rufe } from "../api/client";
import type { Impressum } from "@shared/schemas/rechtliches";
import { formatTag } from "@shared/zeit";

type Daten = { freigegeben: false } | { freigegeben: true; impressum: Impressum; datenschutzText: string; stand: string };

export function useRechtliches() {
  return useQuery({ queryKey: ["public", "rechtliches"], queryFn: () => rufe<Daten>(api.api.public.rechtliches.$get()), staleTime: 600_000 });
}

/** Text mit „# “/„## “ und Absätzen darstellen (wie die Vertragsvorlage). */
export function TextAbsaetze({ text }: { text: string }) {
  return (
    <div className="sc-doc">
      {text.split(/\n{2,}/).map((abs, i) => {
        const z = abs.trim();
        if (z.startsWith("# ")) return <h1 key={i} className="title">{z.slice(2)}</h1>;
        if (z.startsWith("## ")) return <h2 key={i} className="heading" style={{ marginTop: "var(--space-4)" }}>{z.slice(3)}</h2>;
        return <p key={i}>{z}</p>;
      })}
    </div>
  );
}

export function RechtlicheSeite() {
  const art = useLocation().pathname.includes("datenschutz") ? "datenschutz" : "impressum";
  const q = useRechtliches();
  return (
    <div className="app-public">
      <div className="app-public__card" style={{ maxWidth: 720 }}>
        <div className="app-public__brand">
          <Marke size={40} />
          <span className="sc-strong">MeinRegalladen</span>
        </div>
        <Seitenzustand query={q} lines={6}>
          {(d) =>
            !d.freigegeben ? (
              <>
                <h1 className="title">{art === "datenschutz" ? "Datenschutzerklärung" : "Impressum"}</h1>
                <p className="sc-muted">Diese Seite wird gerade erstellt. Fragen beantwortet der Anbieter per E-Mail.</p>
              </>
            ) : art === "datenschutz" ? (
              <>
                <TextAbsaetze text={d.datenschutzText} />
                <p className="sc-muted sc-small">Stand {formatTag(d.stand.slice(0, 10))}</p>
              </>
            ) : (
              <>
                <h1 className="title">Impressum</h1>
                <p>Angaben gemäß § 5 DDG</p>
                <p className="sc-strong" style={{ margin: 0 }}>{d.impressum.firma}</p>
                {d.impressum.vertretenDurch ? <p style={{ margin: 0 }}>vertreten durch {d.impressum.vertretenDurch}</p> : null}
                <p style={{ margin: 0 }}>{d.impressum.strasse}</p>
                <p>{[d.impressum.plz, d.impressum.ort].filter(Boolean).join(" ")}</p>
                {d.impressum.email ? <p style={{ margin: 0 }}>E-Mail: <a href={`mailto:${d.impressum.email}`}>{d.impressum.email}</a></p> : null}
                {d.impressum.telefon ? <p style={{ margin: 0 }}>Telefon: {d.impressum.telefon}</p> : null}
                {d.impressum.ustId ? <p style={{ margin: "var(--space-3) 0 0" }}>Umsatzsteuer-Identifikationsnummer: {d.impressum.ustId}</p> : null}
                {d.impressum.register ? <p style={{ margin: 0 }}>{d.impressum.register}</p> : null}
                {d.impressum.inhaltlichVerantwortlich ? <p style={{ margin: "var(--space-3) 0 0" }}>Inhaltlich verantwortlich: {d.impressum.inhaltlichVerantwortlich}</p> : null}
                {d.impressum.datenschutzbeauftragter ? <p style={{ margin: 0 }}>Datenschutz: {d.impressum.datenschutzbeauftragter}</p> : null}
              </>
            )
          }
        </Seitenzustand>
        <p className="sc-muted sc-small" style={{ marginTop: "var(--space-4)" }}>
          <Link to="/impressum">Impressum</Link> · <Link to="/datenschutz">Datenschutz</Link> · <Link to="/anmelden">Anmelden</Link>
        </p>
      </div>
    </div>
  );
}

/** Kleine Fußzeile mit den beiden Links – für Anmeldeseite, Regalseite, Portale. */
export function RechtlichesFuss() {
  return (
    <p className="sc-muted sc-small sc-center" style={{ margin: "var(--space-3) 0 0" }}>
      <Link to="/impressum">Impressum</Link> · <Link to="/datenschutz">Datenschutz</Link>
    </p>
  );
}
